import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { Post } from './Post'
import { fullName } from '@app/util/helpers'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { PostViewModel } from 'functions/app/thread/view'
import { PostModel, ThreadModel } from '@app/types/Thread'
import { Empty } from '@app/components/blocks/empty/Empty'
import SearchForGroupImage from '@app/assets/images/search-for-group.png'

const mapPost = (post: PostViewModel): PostModel => {
  return {
    id: post.id,
    authorName: post.author
      ? fullName({
          firstName: post.author.firstName,
          lastName: post.author.lastName,
          middlePart: post.author.middlePart,
        })
      : '',
    authorAvatar: post.author?.avatar?.src,
    creationDate: new Date(post.createdAt),
    content: post.content,
    media: post.media.filter((m) => m.status === 'ready').map((m) => m.src),
    reactionUserIds: post.viewers
      .filter((v: { $reaction: string }) => v.$reaction === 'like')
      .map((v: { id: any }) => v.id.toString()),
    comments: post.replies ? post.replies.map(mapPost) : [],
    hasMoreComments: false,
  }
}

export const Thread = ({ threadId }: { threadId: number }) => {
  const [newPost, setNewPost] = useState<string>('')
  const [postMedia, setPostMedia] = useState<File[]>([])
  const [mounted, setMounted] = useState(false)
  const [clientThread, setClientThread] = useState<ThreadModel | null>(null)

  const translations = useTranslations().app.plotPage.threadTab
  const client = useClient()
  const { withAuthCheck } = useAuthModal()
  const threadQuery = useQuery('view-thread', { id: threadId })
  const serverThread: ThreadModel | null = useMemo(() => {
    if (!threadQuery.data) {
      return null
    }
    return {
      id: threadQuery.data.id,
      posts: threadQuery.data.posts ? threadQuery.data.posts.map(mapPost) : [],
    }
  }, [threadQuery.data])

  const thread = mounted ? clientThread : serverThread

  useEffect(() => {
    client.query('view-thread', { id: threadId }).subscribe((result) => {
      setClientThread({
        id: result.id,
        posts: result.posts ? result.posts.map(mapPost) : [],
      })
      setMounted(true)
    })
    client.call('read-thread', { threadId }).catch(() => {})
  }, [client, threadId])

  const post = () => {
    void withAuthCheck(async () => {
      const trimmed = newPost.trim()
      if (!trimmed && postMedia.length === 0) {
        return
      }
      const result = await client.call('add-post', {
        threadId: thread.id,
        content: trimmed,
      })
      if (result) {
        uploadFile(result)
        setNewPost('')
      }
    })
  }
  const comment = async (postId: number, text: string): Promise<boolean> => {
    return withAuthCheck(async () => {
      return await client.call('add-post', {
        threadId: thread.id,
        parentPostId: postId,
        content: text,
      })
    })
  }
  const react = (postId: number) => {
    void withAuthCheck(async () => {
      await client.call('react-post', {
        postId: postId,
      })
    })
  }
  const uploadFile = useCallback(
    (postId: number) => {
      postMedia.forEach((file) => {
        void client.stream('upload-file', {
          mimeType: file.type,
          contents: file,
          size: file.size,
          fileName: `post-media-${file.name}-${Date.now()}`,
          payload: { postMedia: { id: postId } },
        })
      })
      setPostMedia([])
    },
    [postMedia, client],
  )
  return (
    <Column gap="1rem" alignItems="stretch">
      <TextInput
        placeholder={translations.postPlaceholder}
        text={newPost}
        setText={setNewPost}
        onSubmit={post}
        fileSelector={{
          filesSelected: postMedia.length,
          maxFiles: 5,
          onFileSelectionUpdated: (files) => setPostMedia(files),
        }}
      />
      {thread?.posts?.length ? (
        thread.posts.map((post, index) => (
          <Post
            key={index}
            post={post}
            onReply={comment}
            onReact={(postId) => {
              react(postId)
            }}
            onLoadMoreComments={function (_postId: number): void {
              throw new Error('Function not implemented.')
            }}
          />
        ))
      ) : (
        <Empty
          title={translations.noPostsTitle}
          style={{ marginTop: '4rem' }}
          imageSrc={SearchForGroupImage}
        >
          {translations.noPostsDescription}
        </Empty>
      )}
    </Column>
  )
}
